import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION, JOB_MANAGER_API_HEADERS } from 'constants/common';
import {
  CreateCustomAmountTrackingPayload,
  CreateCustomAmountTrackingResponse,
  CustomerByIdPayload,
  CustomerDetails,
  DeleteInvoicePayload,
  DiscountSettings,
  EditInvoicePayload,
  EditInvoicePayloadV2,
  GetCustomersPayload,
  GetCustomersResponse,
  GetInvoiceAttachmentsResponse,
  GetInvoiceCategoryItemListingResponse,
  GetInvoiceCategoryItemListingsPayload,
  GetInvoiceQRImagePayload,
  GetInvoiceQRImageResponse,
  GetInvoiceRefundableTransactionsPayload,
  GetInvoiceRefundableTransactionsResponse,
  GetInvoiceSearchSuggestionPayload,
  GetInvoiceSearchSuggestionResponse,
  GetInvoicesPayload,
  GetInvoicesResponse,
  GetInvoicesV2Payload,
  GetInvoicesV2Response,
  GetMerchantsPayload,
  IInvoiceServiceCharge,
  IInvoiceServiceChargeFromResponse,
  ImageUploadResponse,
  InvoiceByIdPayload,
  InvoiceByIdResponse,
  InvoiceByIdResponseV2,
  InvoiceDownloadPayload,
  InvoicePayload,
  InvoicePayloadV2,
  InvoiceReminder,
  InvoiceResponse,
  InvoiceResponseV2,
  LineItem,
  LineItemTaxFromAPIResponse,
  MarkPaidPayload,
  MarkPaidResponse,
  Merchant,
  ShareInvoicePayload,
  ShareInvoiceResponse,
  TaxesAndFeesPayload,
  TaxesAndFeesResponse
} from './invoices.model';
import { FileTypes } from 'types/baseTypes';
import { downloadFile, printFile } from 'utils/commonUtils';
import { InvoiceSurchargeState, TaxesAndFeesPayloadState, updateInvoiceFeeSettings } from './InvoicesSlice';
import {
  InvoiceLineItemEditPage,
  InvoicesEditSliceInitPayload,
  TaxesAndFeesPayloadStateEditPage,
  initialiseInvoiceEditSlice,
  initialiseInvoiceEditSliceFromV2,
  updateInvoiceFeeSettingsEditPage
} from './edit-invoice/invoicesEditSlice';
import { updateInvoiceByIdRawDataV2, updateInvoiceData } from '../payments-side-panel/sidePanelSlice';
import { LineItemAdditionalTax } from './types';
import { getUrlSearchParams, getUrlSearchParamsWithArrays } from 'utils/apiUtils';
import {
  initialiseJobDetailsFromInvoice,
  initialiseJobDetailsFromInvoiceV2,
  updateInvoiceFeeSettingsInJobs
} from 'containers/jobs/create-edit-job/createJobSlice';
import {
  initializeEstimateFromInvoice,
  initializeEstimateFromInvoiceV2,
  updateInvoiceFeeSettingsInEstimates,
  updateIsInitialisingEstimateFromAPI
} from 'containers/estimates/estimatesSlice'; // Import the action
import { TipTimingEnum } from './constants';
import { prepareInvoiceAttachments } from './utils';

// TODO Move these fns to a different file
export const parseLineItemTaxesFromResponse = (taxes: LineItemTaxFromAPIResponse[]): LineItemAdditionalTax[] => {
  if (taxes) {
    const lineItemLevelTaxes: LineItemAdditionalTax[] = taxes.map((tax, index) => {
      return {
        id: tax?.catalogueTaxId || index,
        name: tax?.name,
        percentage: tax?.percentage,
        amount: +tax?.amount
      };
    });
    return lineItemLevelTaxes;
  }

  return [];
};

const prepareInvoiceLineItems = (lineItemList: LineItem[]) => {
  if (lineItemList === null) {
    return null;
  }
  const invoiceLineItemList: InvoiceLineItemEditPage[] = lineItemList.map((lineItem, index) => {
    return {
      id: index.toString(),
      catalogueLineItemId: lineItem.catalogueLineItemId,
      itemDescription: lineItem.name,
      additionalDetails: lineItem.additionalDetails,
      quantity: lineItem.qty.toString(),
      total: +lineItem.unitPrice * +lineItem.qty,
      unitCost: lineItem.unitPrice,
      originalUnitCost: lineItem.unitCost,
      markupPercentage: lineItem.markupPercentage,
      taxes: parseLineItemTaxesFromResponse(lineItem?.taxes),
      sku: lineItem?.sku ?? ''
    };
  });

  return invoiceLineItemList;
};

export const prepareInvoiceServiceCharge = ({
  serviceCharge = {
    id: 0,
    name: '',
    amount: '',
    percentage: '',
    taxes: []
  }
}: {
  serviceCharge: IInvoiceServiceChargeFromResponse;
}): IInvoiceServiceCharge => {
  const invoiceServiceCharge: IInvoiceServiceCharge = {
    id: serviceCharge?.id,
    name: serviceCharge?.name,
    amount: serviceCharge?.amount,
    percentage: serviceCharge?.percentage,
    taxes:
      serviceCharge?.taxes?.map(tax => ({ id: tax?.catalogueTaxId, name: tax?.name, percentage: tax?.percentage })) ??
      []
  };
  return invoiceServiceCharge;
};

const InvoicesBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getCategoryItemList: builder.query<GetInvoiceCategoryItemListingResponse, GetInvoiceCategoryItemListingsPayload>({
      query: params => {
        let url = `${API_VERSION.V1}/catalogue/invoice-listings?`;
        const urlParams = getUrlSearchParams(params);
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      }
    }),

    getInvoices: builder.query<GetInvoicesResponse, GetInvoicesPayload>({
      query: params => {
        let url = `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices?`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            params[key].map(item => {
              urlParams.append(key, item);
            });
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch invoices data',
        showToast: true
      }
    }),

    getInvoicesV2: builder.query<GetInvoicesV2Response, GetInvoicesV2Payload>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParams(queryParams);
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices?${urlParams.toString()}`;
        return {
          url: url,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.INVOICES_LIST],
      extraOptions: {
        failure: 'failed to fetch invoices data',
        showToast: true
      }
    }),

    exportInvoices: builder.query<void, GetInvoicesV2Payload>({
      query: params => {
        const { teamId, ...queryParams } = params;
        const urlParams = getUrlSearchParamsWithArrays(queryParams);
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/export?${urlParams.toString()}`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.CSV, fileName: 'invoices' });
          },
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'Failed to export invoices',
        success: 'Invoices exported successfully!',
        showToast: true
      }
    }),

    downloadInvoiceList: builder.query<any, GetInvoicesPayload>({
      query: params => {
        let url = `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices?`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            params[key].map(item => {
              urlParams.append(key, item);
            });
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: params.type, fileName: 'invoices' });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice list',
        success: 'Invoices exported successfully!',
        showToast: true
      }
    }),

    downloadInvoiceAsPDF: builder.query<any, InvoiceDownloadPayload>({
      query: ({ invoiceId }) => {
        const url = `${API_VERSION.V7}/public/invoices/${invoiceId}/download`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await downloadFile({ response, fileType: FileTypes.PDF, fileName: 'invoice' });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice pdf',
        success: 'Invoice exported successfully!',
        showToast: true
      }
    }),

    downloadPrintInvoiceAsPDF: builder.query<any, InvoiceDownloadPayload>({
      query: ({ invoiceId }) => {
        const url = `${API_VERSION.V7}/public/invoices/${invoiceId}/download`;
        return {
          url: url,
          method: 'GET',
          responseHandler: async response => {
            await printFile({ response });
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice pdf',
        showToast: true
      }
    }),

    getCustomers: builder.query<GetCustomersResponse, GetCustomersPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_VERSION.V2}/merchants/customers?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch customer data'
      }
    }),
    // used in filters
    getAllCustomers: builder.query<GetCustomersResponse, GetCustomersPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_VERSION.V3}/merchants/customers?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch customer data'
      }
    }),
    getInvoiceSearchSuggestions: builder.query<GetInvoiceSearchSuggestionResponse, GetInvoiceSearchSuggestionPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices/search-suggestion?teamId=${teamId}`,
          method: 'GET'
        };
      }
    }),

    shareInvoice: builder.mutation<ShareInvoiceResponse, ShareInvoicePayload>({
      query: ({ teamId, invoiceId, ...payload }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/share`,
          method: 'POST',
          body: payload
        };
      },
      invalidatesTags: [API_TAG_TYPES.INVOICES_LIST],
      extraOptions: {
        failure: 'Something went wrong, please try again.'
      }
    }),

    createCustomAmountTracking: builder.mutation<CreateCustomAmountTrackingResponse, CreateCustomAmountTrackingPayload>(
      {
        query: ({ invoiceId, requestedAmount, teamId }) => {
          return {
            url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/set-custom-amount`,
            method: 'POST',
            body: { requestedAmount }
          };
        },
        extraOptions: {
          failure: 'Failed to create invoice tracking'
        }
      }
    ),

    markPaid: builder.mutation<MarkPaidResponse, MarkPaidPayload>({
      query: ({ invoiceId, paymentMethod, ...payload }) => {
        return {
          url: `${API_VERSION.V2}/merchants/invoices/${invoiceId}/mark-paid`,
          method: 'POST',
          body: {
            invoiceId,
            PaymentMethod: paymentMethod,
            ...payload
          }
        };
      },
      invalidatesTags: [
        API_TAG_TYPES.JOB_DETAILS,
        API_TAG_TYPES.SUGGESTED_ADJUSTMENTS_FOR_JOB,
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY
      ]
    }),

    deleteInvoice: builder.mutation<any, DeleteInvoicePayload>({
      query: ({ invoiceId, teamId }) => {
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/invoices/${invoiceId}`,
          method: 'DELETE'
        };
      },
      invalidatesTags: [
        API_TAG_TYPES.CUSTOMERS,
        API_TAG_TYPES.INVOICES_LIST,
        API_TAG_TYPES.GET_PROFITABILITY_SUMMARY,
        API_TAG_TYPES.JOB_DETAILS
      ]
    }),
    getInvoiceById: builder.query<InvoiceByIdResponse, InvoiceByIdPayload>({
      query: params => {
        return {
          // eslint-disable-next-line max-len
          url: `${API_VERSION.V5}/merchants/invoices/${params.invoiceId}?includeTransactions=${
            params.includeTransactions ?? true
          }&teamId=${params.teamId}`,
          method: 'GET',
          headers: JOB_MANAGER_API_HEADERS
        };
      },

      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const { isFromEdit = false, isFromJob = false, isFromEstimate = false } = params;

        if (isFromEstimate) {
          dispatch(updateIsInitialisingEstimateFromAPI(true));
          dispatch(initializeEstimateFromInvoice(data)); // Dispatch the action with the invoice data
        }

        if (isFromEdit || isFromJob) {
          const initialEditSliceState: InvoicesEditSliceInitPayload = {
            invoiceCustomerEditPage: {
              id: data?.customer?.id,
              ID: data?.customer?.ID ?? data?.customer?.id,
              name: data?.customer?.name,
              phoneNumber: data?.customer?.phoneNumber,
              email: data?.customer?.email,
              merchantId: data?.customer?.merchantId,
              billingAddress: data?.customer?.billingAddress,
              lastName: data?.customer?.lastName,
              serviceAddress: data?.customer?.serviceAddress,
              companyName: data?.customer?.companyName
            },
            additionalTaxesEditPage: null,
            additionalTaxEnabledEditPage: null,
            discountSettingsEditPage: { enabled: null },
            invoiceLineItemsEditPage: prepareInvoiceLineItems(data?.lineItems),
            // use surcharge from  taxes and fees api, don't pre-populate
            currentSurchargeEditPage: {
              percentageWithBps: null,
              valueInCurrencyWithBps: null,
              percentage: null,
              enabled: null,
              basePoints: null,
              valueInCurrency: null,
              config: null
            },
            lineItemsTotalEditPage: null,
            invoiceSubTotalEditPage: null,
            lineItemsStateCountEditPage: data?.lineItems?.length,

            invoiceAttachmentIdCountEditPage: data?.attachments?.length,
            discountFormDataEditPage: {
              discountPercent: data?.discountPercentage,
              discountAmount: data?.discount,
              isDiscountAmountSelected: data?.isDiscountAmountSelected,
              discountInCurrency: data?.discount,
              amountAfterDiscount: null
            },
            serviceCharge: prepareInvoiceServiceCharge({ serviceCharge: data?.serviceCharges?.[0] }),
            commentEditPage: data?.comment,
            selectedInvoiceAttachmentsEditPage: prepareInvoiceAttachments(data?.attachments),
            tipEnabledEditPage: data?.hasTip,
            invoiceEditedResponse: null,
            finalAmountEditPage: null,
            invoiceOptionsEditPage: {
              createdAt: data?.createdAt,
              dueDate: data?.dueDate
            },
            invoiceNumber: data?.invoiceNumber,
            isSignatureEnabled: data?.isSignatureEnabled,
            isServiceFeeEnabled: data?.isServiceFeeEnabled,
            jobData: data.jobData,
            invoiceStatus: data.invoiceStatus,
            paymentModesFromInvoice: data.paymentModes,
            estimateStatus: data.estimates?.[0]?.status,
            estimateNumber: data.estimates?.[0]?.estimateNumber,
            estimateData: data.estimates?.[0],
            invoiceId: data.invoiceId
          };
          if (isFromEdit) {
            dispatch(initialiseInvoiceEditSlice(initialEditSliceState));
          }
          if (isFromJob) {
            dispatch(initialiseJobDetailsFromInvoice(initialEditSliceState));
          }
        }
        dispatch(updateInvoiceData(data));
      },
      providesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Something went wrong'
      }
    }),

    getInvoiceByIdV2: builder.query<InvoiceByIdResponseV2, InvoiceByIdPayload>({
      query: params => {
        let url = `${API_VERSION.V7}/teams/${params.teamId}/invoices/${params.invoiceId}`;
        if (params.timezone) {
          url += `?timezone=${params.timezone}`;
        }
        return {
          url: url,
          method: 'GET'
        };
      },

      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const { isFromEdit = false, isFromJob = false, isFromEstimate = false } = params;

        // Store raw V2 response
        dispatch(updateInvoiceByIdRawDataV2(data));

        if (isFromEstimate) {
          dispatch(updateIsInitialisingEstimateFromAPI(true));
          dispatch(initializeEstimateFromInvoiceV2(data));
        }

        if (isFromEdit) {
          dispatch(initialiseInvoiceEditSliceFromV2(data));
        }

        if (isFromJob) {
          dispatch(initialiseJobDetailsFromInvoiceV2(data));
        }

        //TODO: check the use case for this and remove
        // Transform V2 data to V1 format for updateInvoiceData
        const transformedDataForSidePanel = {
          ...data.invoice,
          customer: data.invoice.invoiceCustomer,
          comment: data.invoice.note,
          hasTip: data.invoice.tip.enabled,
          discountPercentage:
            data.invoice.discount.enabled && data.invoice.discount.type === 'PERCENTAGE'
              ? data.invoice.discount.value.toString()
              : '0',
          discount: data.invoice.discount.enabled ? data.invoice.discount.amount.toString() : '0',
          isDiscountAmountSelected: data.invoice.discount.enabled && data.invoice.discount.type === 'AMOUNT',
          lineItems: data.invoice.lineItems,
          serviceCharges: data.invoice.serviceCharges,
          attachments: data.invoice.attachments,
          paymentModes: data.invoice.paymentMethodList, // Keep as is - no transformation needed
          partialPaymentEnabled: data.invoice.partialPaymentEnabled,
          paymentRemindersEnabled: data.invoice.reminderEnabled,
          paymentReminders: [], // TODO: Transform reminders if available in V2
          tipTiming: data.invoice.tip.type || TipTimingEnum.ALL_PAYMENTS
        };
        dispatch(updateInvoiceData(transformedDataForSidePanel as any));
      },
      providesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Something went wrong'
      }
    }),

    getCustomerById: builder.query<CustomerDetails, CustomerByIdPayload>({
      query: params => {
        const { customerId, teamId } = params;
        return {
          url: `${API_VERSION.V3}/merchants/customers/${customerId}?teamId=${teamId}`,
          method: 'GET'
        };
      },

      extraOptions: {
        failure: 'Failed to fetch customer data'
      }
    }),

    getTaxesAndFees: builder.query<TaxesAndFeesResponse, TaxesAndFeesPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/teams/taxes-and-fees?teamId=${teamId}`,
          method: 'GET'
        };
      },

      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const surchargeState: InvoiceSurchargeState = {
          percentage: data?.surcharge?.percentage,
          valueInCurrency: null,
          percentageWithBps: null,
          valueInCurrencyWithBps: null,
          enabled: data?.surcharge?.enabled,
          basePoints: data?.surcharge?.basePoints,
          config: data?.surcharge?.config
        };
        const discountSettings: DiscountSettings = { enabled: data?.discount.enabled };

        dispatch(
          updateInvoiceFeeSettings({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
        dispatch(
          updateInvoiceFeeSettingsEditPage({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadStateEditPage)
        );
        dispatch(
          updateInvoiceFeeSettingsInJobs({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
        dispatch(
          updateInvoiceFeeSettingsInEstimates({
            surchargeState,
            discountSettings
          } as TaxesAndFeesPayloadState)
        );
      },
      extraOptions: {
        failure: 'Failed to get taxes and fees'
      }
    }),
    getInvoicePDFPreview: builder.query<any, { teamId: string; payload: any }>({
      query: ({ teamId, payload }) => {
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/preview/pdf`;
        return {
          url: url,
          method: 'POST',
          body: payload,
          responseHandler: async response => {
            const blob = await response.blob();
            return blob;
          },
          // Do not cache responses for pdf's or csv's as it may lead to crashing the browser
          cache: 'no-cache'
        };
      },
      extraOptions: {
        failure: 'failed to download invoice preview pdf',
        showToast: true
      }
    }),

    // TODO define payload type
    uploadFile: builder.mutation<ImageUploadResponse, { file: any; teamId: number }>({
      query: ({ teamId, file }) => {
        const formData = new FormData();
        formData.append('teamId', teamId.toString());
        formData.append('image', file);

        return {
          url: `${API_VERSION.V2}/merchants/invoices/upload-images`,
          method: 'POST',
          body: formData,
          headers: {
            // make sure not to set the Content-Type header.
            // The browser will set it for you, including the boundary parameter.
            // 'Content-Type': 'multipart/form-data'
          }
        };
      },

      extraOptions: {
        failure: 'We are unable to upload your image. Try again after sometime.',
        showToast: true
      }
    }),

    updateInvoiceReminders: builder.mutation<
      { success: boolean; message: string },
      { teamId: number; invoiceId: string; reminders: InvoiceReminder[] }
    >({
      query: ({ teamId, invoiceId, reminders }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/reminders`,
          method: 'PATCH',
          body: { reminders }
        };
      },
      invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID],
      extraOptions: {
        failure: 'Failed to update invoice reminders',
        showToast: true,
        success: 'Invoice reminders updated successfully'
      }
    }),

    createInvoice: builder.mutation<InvoiceResponse, InvoicePayload>({
      query: payload => {
        return {
          url: `${API_VERSION.V6}/merchants/invoices`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Failed to create invoice',
        showToast: false,
        success: 'Invoice created successfully'
      }
    }),

    createInvoiceV2: builder.mutation<InvoiceResponseV2, { teamId: string; payload: InvoicePayloadV2 }>({
      query: ({ teamId, payload }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Failed to create invoice',
        showToast: false,
        success: 'Invoice created successfully'
      }
    }),

    editInvoice: builder.mutation<InvoiceResponse, EditInvoicePayload>({
      query: payload => {
        return {
          url: `${API_VERSION.V3}/merchants/invoices/${payload.invoiceId}`,
          method: 'PUT',
          body: payload.invoicePayload
        };
      },
      extraOptions: {
        failure: 'Failed to update invoice',
        showToast: false,
        success: 'Invoice edited successfully'
      }
    }),

    editInvoiceV2: builder.mutation<InvoiceResponseV2, { teamId: string; payload: EditInvoicePayloadV2 }>({
      query: ({ teamId, payload }) => {
        return {
          url: `${API_VERSION.V7}/teams/${teamId}/invoices/${payload.invoiceId}`,
          method: 'PUT',
          body: payload.invoicePayload
        };
      },
      extraOptions: {
        failure: 'Failed to update invoice',
        showToast: false,
        success: 'Invoice updated successfully'
      }
    }),
    getMerchantsByTeamIdInvoice: builder.query<Merchant[], GetMerchantsPayload>({
      query: ({ teamId }) => {
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/invoices/filters/team-members?teamId=${teamId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to merchant tile data'
      }
    }),

    getInvoiceAttachments: builder.query<GetInvoiceAttachmentsResponse, { teamId: number; invoiceId: string }>({
      query: ({ teamId, invoiceId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/invoices/${invoiceId}/attachments`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch invoice attachments',
        showToast: false
      }
    }),

    getInvoiceQRImage: builder.mutation<GetInvoiceQRImageResponse, GetInvoiceQRImagePayload>({
      query: ({ teamId, invoiceId, requestedAmount }) => ({
        url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/qr`,
        method: 'POST',
        body: { requestedAmount }
      }),
      extraOptions: {
        failure: 'Failed to generate QR image',
        showToast: true
      }
    }),

    getInvoiceRefundableTransactions: builder.query<
      GetInvoiceRefundableTransactionsResponse,
      GetInvoiceRefundableTransactionsPayload
    >({
      query: params => {
        const { teamId, invoiceId } = params;
        const url = `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/refundable-transactions`;
        return {
          url: url,
          method: 'GET'
        };
      },
      keepUnusedDataFor: 0,
      extraOptions: {
        failure: 'Failed to fetch invoice transactions',
        showToast: true
      }
    }),

    sendInvoiceReminder: builder.mutation<{ success: boolean; message: string }, { teamId: string; invoiceId: string }>(
      {
        query: ({ teamId, invoiceId }) => {
          return {
            url: `${API_VERSION.V7}/teams/${teamId}/invoices/${invoiceId}/send-reminder`,
            method: 'POST'
          };
        },
        invalidatesTags: [API_TAG_TYPES.INVOICE_BY_ID],
        extraOptions: {
          failure: 'Failed to send invoice reminder',
          showToast: true,
          success: 'Reminder sent successfully'
        }
      }
    )
  })
});

export const {
  useCreateInvoiceMutation,
  useCreateInvoiceV2Mutation,
  useEditInvoiceMutation,
  useEditInvoiceV2Mutation,
  useUploadFileMutation,
  useUpdateInvoiceRemindersMutation,
  useGetTaxesAndFeesQuery,
  useLazyDownloadInvoiceListQuery,
  useLazyDownloadPrintInvoiceAsPDFQuery,
  useLazyDownloadInvoiceAsPDFQuery,
  useLazyGetInvoicesQuery,
  useLazyGetInvoicesV2Query,
  useLazyExportInvoicesQuery,
  useLazyGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdV2Query,
  useGetInvoiceByIdV2Query,
  useLazyGetCustomerByIdQuery,
  useGetInvoiceSearchSuggestionsQuery,
  useShareInvoiceMutation,
  useCreateCustomAmountTrackingMutation,
  useMarkPaidMutation,
  useDeleteInvoiceMutation,
  useLazyGetCustomersQuery,
  useLazyGetInvoicePDFPreviewQuery,
  useLazyGetMerchantsByTeamIdInvoiceQuery,
  useLazyGetAllCustomersQuery,
  useLazyGetCategoryItemListQuery,
  useGetInvoiceAttachmentsQuery,
  useLazyGetInvoiceAttachmentsQuery,
  useGetInvoiceQRImageMutation,
  useGetInvoiceRefundableTransactionsQuery,
  useLazyGetInvoiceRefundableTransactionsQuery,
  useSendInvoiceReminderMutation
} = InvoicesBaseApi;

export const invoicesApi = InvoicesBaseApi;
